import { useEffect, useMemo, useRef, useState } from "react";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { shortUuid, type Container } from "@egalregal/shared";
import { useT } from "../../shared/i18n";
import { log } from "../../logger";
import type { CodeDecoder, ScanFormat } from "./scan";
import {
  manualFallbackOf,
  outcomeForResolution,
  resolveScan,
  type ResolveDeps,
} from "./resolution";
import { useScanHistoryEntry } from "./useScanHistoryEntry";
import { useQrScanner } from "./useQrScanner";
import { ScanChip } from "./ScanChip";
import { ScanFallback } from "./ScanFallback";

export interface IdentifyPresetProps {
  /** Called with the chosen code; the caller performs the write/close. */
  onResolved: (code: string) => Promise<void> | void;
  onClose: () => void;
  /** Explanatory text above the camera (the caller's link hint). */
  hint?: string;
  /** Resolve a code to a display name; undefined means it cannot be used. */
  resolveLabel?: (code: string) => string | undefined;
  /** Copy shown when a label is unknown or the caller's action fails. */
  errorMessage?: string;
  decoder?: CodeDecoder;
}

/**
 * `identify` preset (scanner-surface 03): resolve a scanned label for a
 * caller, without writing anything.
 *
 * One-shot per code: a decode is resolved to the caller's label and offered as
 * a confirmation action; the same code is not re-resolved, but aiming at a new
 * one replaces the offer. Typed UUID entry reaches the same path as the
 * camera. No create chooser exists here — an unknown code is an error with the
 * caller's message. `onResolved` runs only when the user confirms, so the
 * caller keeps ownership of the write (the container-detail link flow) and of
 * closing the dialog after success.
 */
export function IdentifyPreset({
  onResolved,
  onClose,
  hint,
  resolveLabel,
  errorMessage,
  decoder,
}: IdentifyPresetProps) {
  const t = useT();
  const history = useScanHistoryEntry();
  const { result, format, cameraError, videoRef, scanning } = useQrScanner(decoder, true, "both");
  const [manualId, setManualId] = useState("");
  const [identified, setIdentified] = useState<{ code: string; name?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const handled = useRef<string | null>(null);
  // identify resolves against the caller's lookup only: it never asks the
  // server, and a product barcode or URL is not a container for this dialog.
  const resolverDeps = useMemo<ResolveDeps>(
    () => ({
      resolveLabel: async (code) => {
        const name = resolveLabel?.(code);
        if (name === undefined) return "not-found";
        return { kind: "container", container: namedLabel(code, name), source: "cache" };
      },
      lookupProduct: async () => "not-found",
    }),
    [resolveLabel],
  );

  const resolve = async (raw: string, scanFormat: ScanFormat = "manual") => {
    // Every decode and typed submission lands in the per-user history; this
    // preset resolves a container for its caller (results-04). Labels are
    // accepted case-insensitively but normalized to lowercase (ADR-0003);
    // non-label payloads stay exactly as scanned.
    const entry = history.record(raw, scanFormat);
    const { payload } = entry;
    const code = entry.code;
    if (!code) return;
    setError(null);
    setManualId("");
    if (!resolveLabel) {
      // Preserved branch: without the caller's lookup identify cannot verify
      // or name the code, so it confirms the scanned code as the target.
      entry.outcome({ kind: "container", id: code });
      setIdentified({ code });
      return;
    }
    const resolution = await resolveScan(payload, resolverDeps);
    entry.outcome(outcomeForResolution(resolution));
    const fallback = manualFallbackOf(resolution);
    if (fallback !== null || resolution.kind !== "container") {
      // The caller's message covers an unknown code and everything that is not
      // a container label (product barcode, payload, trashed target).
      setIdentified(null);
      setError(errorMessage ?? t("scanNotFound"));
      return;
    }
    setIdentified({ code: resolution.container.id, name: resolution.container.name });
  };

  // One-shot latch per code: the fixture/camera loop repeats the held value,
  // so only a new code starts a new resolution.
  useEffect(() => {
    if (!result) return;
    // The fixture/camera loop repeats the held value verbatim; a new string is
    // a new code. Typed entry reaches the same path without the latch.
    const code = result.trim();
    if (!code || handled.current === code) return;
    handled.current = code;
    void resolve(result, format ?? "unknown");
  }, [result, resolveLabel, errorMessage]);

  const submitManual = () => {
    void resolve(manualId, "manual");
  };

  const confirm = async () => {
    if (!identified || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onResolved(identified.code);
      onClose();
    } catch (err) {
      log.warn(
        { err, code: shortUuid(identified.code) },
        "[scan] Failed to resolve identified scanned code",
      );
      setError(errorMessage ?? t("scanNotFound"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogTitle>{t("scanTitle")}</DialogTitle>
      <DialogContent>
        {hint && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {hint}
          </Typography>
        )}
        <ScanChip
          videoRef={videoRef}
          scanning={scanning}
          cameraError={cameraError}
          ariaLabel={t("scanTitle")}
          cameraErrorText={t("scanCameraError")}
        />
        {identified && (
          <Button
            variant="contained"
            color="success"
            fullWidth
            disabled={busy}
            onClick={() => void confirm()}
            data-testid="identified-link"
            sx={{ mt: 1 }}
          >
            {identified.name
              ? t("scanOpenContainer", { name: identified.name })
              : t("scanSubmit")}
          </Button>
        )}
        <ScanFallback
          value={manualId}
          onChange={setManualId}
          onSubmit={submitManual}
          placeholder={t("scanFallbackLabel")}
          submitLabel={t("scanSubmit")}
          sx={{ mt: 2 }}
        />
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }} data-testid="scan-identify-error">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
      </DialogActions>
    </>
  );
}

/**
 * The container target identify's caller-provided name lookup reports. The
 * prop supplies a display name only, and the dialog never renders container
 * metadata, so identity and name are the only meaningful fields (the resolver
 * needs the domain target, not the row).
 *
 * @param code the canonical label UUID.
 * @param name the caller's display name for it.
 * @returns a cache-sourced container shape carrying that name.
 */
function namedLabel(code: string, name: string): Container {
  return {
    schemaVersion: 1,
    version: 0,
    id: code,
    kind: "label",
    name,
    description: "",
    tags: [],
    children: [],
    files: [],
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date(0).toISOString(),
    deleted: false,
    deletedAt: null,
  };
}
